import apiClient from './apiClient';
import { AssignmentSelection } from './deliverySearchService';

export interface FeedbackInstance {
  id: string;
  feedbackId: string;
  feedbackTitle?: string;
  versionNo: number;
  assignedTo: string[];
  assigneeType: 'user' | 'group' | 'role';
  assignments?: AssignmentSelection[];
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  startDate: string;
  endDate?: string;
  status: 'scheduled' | 'active' | 'completed';
  createdAt: string;
  updatedAt: string;
  hasUserResponded?: boolean;
  customInvitationSubject?: string;
  customInvitationBody?: string;
  customReminderSubject?: string;
  customReminderBody?: string;
  autoSendInvitations: boolean;
}

export interface FeedbackOccurrence {
  id: string;
  feedbackInstanceId: string;
  occurrenceNumber: number;
  startsAt: string;
  endsAt?: string;
  status: string;
  feedbackTitle?: string;
  feedbackId: string;
  versionNo: number;
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  assigneeType: 'user' | 'group' | 'role';
  assignedTo: string[];
  assignments?: AssignmentSelection[];
  hasUserResponded?: boolean;
}

export interface CreateFeedbackInstanceDto {
  feedbackId: string;
  versionNo: number;
  assignments?: AssignmentSelection[];
  assignedTo?: string[];
  assigneeType?: 'user' | 'group' | 'role';
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  startDate: string;
  endDate?: string;
  status: 'scheduled' | 'active' | 'completed';
  autoSendInvitations?: boolean;
}

export interface UpdateFeedbackInstanceDto {
  assignments?: AssignmentSelection[];
  assignedTo?: string[];
  assigneeType?: 'user' | 'group' | 'role';
  frequency?: 'once' | 'daily' | 'weekly' | 'monthly';
  startDate?: string;
  endDate?: string;
  status?: 'scheduled' | 'active' | 'completed';
  autoSendInvitations?: boolean;
}

export interface EmailTemplate {
  invitationSubject: string;
  invitationBody: string;
  reminderSubject: string;
  reminderBody: string;
}

export interface EmailJobAccepted {
  jobId: string;
  totalRecipients: number;
}

export interface EmailSendError {
  recipientEmail: string;
  reason: string;
  attemptedAt: string;
}

export interface EmailRecipient {
  email: string;
  name: string;
  status: 'sent' | 'failed' | 'skipped';
}

export interface EmailSendJobStatus {
  id: string;
  feedbackInstanceId: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalRecipients: number;
  sent: number;
  failed: number;
  skipped: number;
  errors?: EmailSendError[];
  recipients?: EmailRecipient[];
  createdAt: string;
  completedAt?: string;
}

export const feedbackInstanceService = {
  async getAllInstances(): Promise<FeedbackInstance[]> {
    const response = await apiClient.get('/api/feedback-instances');
    return response.data;
  },

  async getInstancesByFeedbackId(feedbackId: string): Promise<FeedbackInstance[]> {
    const response = await apiClient.get(`/api/feedback-instances/feedback/${feedbackId}`);
    return response.data;
  },

  async getInstanceById(id: string): Promise<FeedbackInstance> {
    const response = await apiClient.get(`/api/feedback-instances/${id}`);
    return response.data;
  },

  async createInstance(data: CreateFeedbackInstanceDto): Promise<FeedbackInstance> {
    const response = await apiClient.post('/api/feedback-instances', data);
    return response.data;
  },

  async updateInstance(id: string, data: UpdateFeedbackInstanceDto): Promise<FeedbackInstance> {
    const response = await apiClient.put(`/api/feedback-instances/${id}`, data);
    return response.data;
  },

  async deleteInstance(id: string): Promise<void> {
    await apiClient.delete(`/api/feedback-instances/${id}`);
  },

  async getMyFeedback(): Promise<FeedbackOccurrence[]> {
    const response = await apiClient.get('/api/feedback-instances/my-feedback');
    return response.data;
  },

  async getOccurrences(instanceId: string): Promise<FeedbackOccurrence[]> {
    const response = await apiClient.get(`/api/feedback-instances/${instanceId}/occurrences`);
    return response.data;
  },

  async sendInvitations(instanceId: string, customContent?: { subject: string; body: string }): Promise<EmailJobAccepted> {
    const response = await apiClient.post(`/api/feedback-instances/${instanceId}/send-invitations`, customContent || null);
    return response.data;
  },

  async getEmailTemplate(instanceId: string): Promise<EmailTemplate> {
    const response = await apiClient.get(`/api/feedback-instances/${instanceId}/email-template`);
    return response.data;
  },

  async saveEmailTemplate(instanceId: string, template: EmailTemplate): Promise<EmailTemplate> {
    const response = await apiClient.put(`/api/feedback-instances/${instanceId}/email-template`, template);
    return response.data;
  },

  async sendReminders(instanceId: string, customContent?: { subject: string; body: string }): Promise<EmailJobAccepted> {
    const response = await apiClient.post(`/api/feedback-instances/${instanceId}/send-reminders`, customContent || null);
    return response.data;
  },

  async getEmailStatus(instanceId: string): Promise<EmailSendJobStatus> {
    const response = await apiClient.get(`/api/feedback-instances/${instanceId}/email-status`);
    return response.data;
  },
};

export default feedbackInstanceService;
